import { z } from "zod";

import { cardParagraphAnchorSchema } from "../utils/redline/wire/redline-payload";

export const IcertisConnectedUserSchema = z.object({
  name: z.string(),
  email: z.string(),
});

export type IcertisConnectedUser = z.infer<typeof IcertisConnectedUserSchema>;

export const IcertisAuthSnapshotSchema = z.object({
  authenticated: z.boolean(),
  reason: z.string().nullable().optional(),
  icm_base_url: z.string().nullable().optional(),
  connected_icm_base_url: z.string().nullable().optional(),
  connected_user: IcertisConnectedUserSchema.nullish(),
});

export type IcertisAuthSnapshot = z.infer<typeof IcertisAuthSnapshotSchema>;

export function icertisConnectedUserLabel(connectedUser: IcertisConnectedUser): string {
  return connectedUser.name === connectedUser.email
    ? connectedUser.email
    : `${connectedUser.name} (${connectedUser.email})`;
}

const NullableStringSchema = z.string().nullable().optional();
const NullableBooleanSchema = z.boolean().nullable().optional();
const NullableNumberSchema = z.number().nullable().optional();

export const TemplateOptionSchema = z.object({
  sys_id: z.string(),
  name: z.string(),
  contract_type_name: NullableStringSchema,
  template_code: NullableStringSchema,
  status: NullableStringSchema,
  description: NullableStringSchema,
  is_current_version: NullableBooleanSchema,
  entity_version: NullableNumberSchema,
  effective_from: NullableStringSchema,
  effective_to: NullableStringSchema,
  language_locale: NullableStringSchema,
  date_format: NullableStringSchema,
  time_format: NullableStringSchema,
  is_bilingual_template: NullableBooleanSchema,
  secondary_language_locale: NullableStringSchema,
  secondary_language_date_format: NullableStringSchema,
  secondary_language_time_format: NullableStringSchema,
  primary_template_sys_id: NullableStringSchema,
  primary_template_name: NullableStringSchema,
  primary_template_code: NullableStringSchema,
  primary_template_language: NullableStringSchema,
  primary_template_status: NullableStringSchema,
  version_comments: NullableStringSchema,
  created_by: NullableStringSchema,
  created_date: NullableStringSchema,
  modified_by: NullableStringSchema,
  modified_date: NullableStringSchema,
});

export const SuggestTemplateSelectionSchema = z.object({
  templates: z.array(TemplateOptionSchema),
  reason: z.string().optional(),
});

export const OTHER_INDEX = -1;

export const CitedParagraphCommentSchema = z.object({
  author: z.string(),
  text: z.string(),
});

export const ParagraphCitationSchema = z.object({
  paragraph_id: z.string().min(1),
  text: z.string(),
  anchor: cardParagraphAnchorSchema.nullable(),
  file_id: z.string().nullable().default(null),
  file_name: z.string().nullable().default(null),
  storage_url: z.string().nullable().default(null),
  comments: z.array(CitedParagraphCommentSchema).default([]),
});

const ChoiceOptionSchema = z.object({
  label: z.string().min(1),
  description: z.string().nullish(),
  citations: z.array(ParagraphCitationSchema).optional(),
});

const LookupPathSchema = z.string().min(1).regex(/^\/(?!\/)\S/, "Expected a root-relative lookup path");

const ShowWhenSchema = z.object({
  field_id: z.string().min(1),
  values: z.array(z.string()).min(1),
});

const CollectInputBaseFieldSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  header: z.string().nullish(),
  required: z.boolean().optional(),
  show_when: ShowWhenSchema.nullish(),
  collapsed: z.boolean().optional(),
});

const CollectInputScalarFieldSchema = CollectInputBaseFieldSchema.extend({
  value: z.string().nullish(),
  description: z.string().nullish(),
  citations: z.array(ParagraphCitationSchema).optional(),
});

export const EditorSideSchema = z.enum(["our_side", "counterparty", "unplaced"]);

export const EditorTileSchema = z.object({
  name: z.string().min(1),
  side: EditorSideSchema,
  unsure: z.boolean(),
});

export const CollectInputFieldSchema = z.discriminatedUnion("type", [
  CollectInputScalarFieldSchema.extend({ type: z.literal("text") }),
  CollectInputScalarFieldSchema.extend({ type: z.literal("long_text"), copyable: z.boolean().optional() }),
  CollectInputScalarFieldSchema.extend({ type: z.literal("number") }),
  CollectInputScalarFieldSchema.extend({ type: z.literal("date") }),
  CollectInputScalarFieldSchema.extend({ type: z.literal("currency"), currencies: z.array(z.string().min(1)).min(1) }),
  CollectInputBaseFieldSchema.extend({
    type: z.literal("list"),
    item_type: z.enum(["email", "text"]),
    value: z.array(z.string()).optional(),
  }),
  CollectInputBaseFieldSchema.extend({
    type: z.literal("single_select"),
    options: z.array(ChoiceOptionSchema).optional(),
    options_url: LookupPathSchema.nullish(),
    allow_other: z.boolean().optional(),
    quick_select: z.boolean().optional(),
    strict: z.boolean().optional(),
    narrowed_by_template_selection: z.boolean().optional(),
    value: z.string().nullish(),
  }),
  CollectInputBaseFieldSchema.extend({
    type: z.literal("multi_select"),
    options: z.array(ChoiceOptionSchema).optional(),
    options_url: LookupPathSchema.nullish(),
    allow_other: z.boolean().optional(),
    quick_select: z.boolean().optional(),
    strict: z.boolean().optional(),
  }),
  CollectInputBaseFieldSchema.extend({
    type: z.literal("editor_sides"),
    our_side_label: z.string(),
    counterparty_label: z.string(),
    editors: z.array(EditorTileSchema).min(1),
    party_field_id: z.string().nullish(),
  }),
]);

const CollectInputFieldsSchema = z.array(CollectInputFieldSchema).min(1).superRefine((fields, context) => {
  const indexesById = new Map<string, number>();

  fields.forEach((field, index) => {
    const previousIndex = indexesById.get(field.id);

    if (previousIndex !== undefined) {
      context.addIssue({
        code: "custom",
        path: [index, "id"],
        message: `Field id duplicates field at index ${previousIndex}`,
      });
    }

    indexesById.set(field.id, index);

    if ((field.type === "single_select" || field.type === "multi_select") && !field.options?.length && !field.options_url) {
      context.addIssue({
        code: "custom",
        path: [index, "options"],
        message: `${field.type} fields require options or options_url`,
      });
    }
  });
});

export const CollectInputSubmitSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("send_to_agent") }),
  z.object({ kind: z.literal("send_when_answered") }),
  z.object({ kind: z.literal("open_link"), href_template: z.string().min(1), label: z.string().min(1) }),
]);

const TemplateSelectionConditionSchema = z.object({
  field_id: z.string(),
  operator: z.string(),
  value: z.string().nullish(),
});

export const TemplateSelectionConditionGroupSchema = z.object({
  operator: z.enum(["and", "or"]),
  conditions: z.array(TemplateSelectionConditionSchema),
  get groups() {
    return z.array(TemplateSelectionConditionGroupSchema);
  },
});

const TemplateSelectionRuleSchema = z.object({
  rule_set_name: z.string(),
  rule_name: z.string(),
  templates: z.array(z.string()),
  when: TemplateSelectionConditionGroupSchema,
});

export const TemplateSelectionSchema = z.object({
  rules: z.array(TemplateSelectionRuleSchema),
});

export const CollectInputSchema = z.object({
  title: z.string().nullish(),
  fields: CollectInputFieldsSchema,
  reason: z.string().nullish(),
  submit: CollectInputSubmitSchema.default({ kind: "send_to_agent" }),
  template_selection: TemplateSelectionSchema.nullish(),
});

export type TemplateSelectionCondition = z.infer<typeof TemplateSelectionConditionSchema>;
export type TemplateSelectionConditionGroup = z.infer<typeof TemplateSelectionConditionGroupSchema>;
export type TemplateSelectionRule = z.infer<typeof TemplateSelectionRuleSchema>;
export type TemplateSelection = z.infer<typeof TemplateSelectionSchema>;

export type CitedParagraphComment = z.infer<typeof CitedParagraphCommentSchema>;
export type ParagraphCitation = z.infer<typeof ParagraphCitationSchema>;
export type ChoiceOption = z.infer<typeof ChoiceOptionSchema>;
export type EditorSide = z.infer<typeof EditorSideSchema>;
export type EditorTile = z.infer<typeof EditorTileSchema>;
export type CollectInputField = z.infer<typeof CollectInputFieldSchema>;
export type CollectInputSubmit = z.infer<typeof CollectInputSubmitSchema>;

const SelectFieldValueSchema = z.object({
  type: z.literal("select"),
  selected: z.array(z.number().int()),
  otherText: z.string().optional(),
  selectedLabels: z.array(z.string()).optional(),
});

const TextFieldValueSchema = z.object({
  type: z.literal("text"),
  value: z.string(),
});

const CurrencyFieldValueSchema = z.object({
  type: z.literal("currency"),
  amount: z.string(),
  currency: z.string(),
});

const ListFieldValueSchema = z.object({
  type: z.literal("list"),
  items: z.array(z.string()),
});

const EditorSidesFieldValueSchema = z.object({
  type: z.literal("editor_sides"),
  editors: z.array(EditorTileSchema),
});

const CollectInputFieldValueSchema = z.discriminatedUnion("type", [
  SelectFieldValueSchema,
  TextFieldValueSchema,
  CurrencyFieldValueSchema,
  ListFieldValueSchema,
  EditorSidesFieldValueSchema,
]);

export const CollectInputAnswerSchema = z.object({
  values: z.record(z.string(), CollectInputFieldValueSchema),
});

export type CollectInputFieldValue = z.infer<typeof CollectInputFieldValueSchema>;
export type CollectInputAnswer = z.infer<typeof CollectInputAnswerSchema>;

export const AgreementReferenceSchema = z.object({
  agreement_name: z.string(),
  agreement_date: z.string().nullish(),
  deeplink: z.string().nullish(),
});

export const AgreementSourceValueSchema = z.object({
  agreement_sys_id: z.string(),
  value: z.string(),
  agreement_name: z.string().nullish(),
  agreement_date: z.string().nullish(),
  deeplink: z.string().nullish(),
});

export const AgreementReviewFieldSchema = z
  .object({
    name: z.string(),
    label: z.string(),
    required: z.boolean().optional(),
    type: z.enum(["text", "number", "date", "enum", "currency"]).optional(),
    options: z.array(z.string()).optional(),
    options_url: LookupPathSchema.nullish(),
    currencies: z.array(z.string()).optional(),
    strict: z.boolean().optional(),
    quick_select: z.boolean().optional(),
    value: z.string().optional(),
    sources: z.array(AgreementSourceValueSchema).optional().catch([]),
    alternatives: z.array(AgreementSourceValueSchema).optional().catch([]),
  })
  .refine((field) => field.type !== "enum" || !!field.options?.length || !!field.options_url, {
    message: "enum fields require options or options_url",
    path: ["options"],
  });

export const RenderAgreementReviewFormSchema = z.object({
  title: z.string().nullish(),
  fields: z.array(AgreementReviewFieldSchema).min(1),
  reason: z.string().nullish(),
  agreements: z.record(z.string(), AgreementReferenceSchema).optional().catch({}),
  default_view: z.enum(["suggested", "by_source"]).optional(),
});

export type AgreementReference = z.infer<typeof AgreementReferenceSchema>;
export type AgreementSourceValue = z.infer<typeof AgreementSourceValueSchema>;
export type AgreementReviewField = z.infer<typeof AgreementReviewFieldSchema>;

function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:"
  } catch {
    return false
  }
}

const HttpsUrlSchema = z.url().refine(isHttpsUrl, {
  message: "Expected an HTTPS URL",
})

export const ICERTIS_UPLOAD_NOTE_MAX_LENGTH = 1000;

export const IcertisUploadRequestSchema = z.object({
  fileContent: z.string(),
  fileName: z.string(),
  fileExtension: z.string(),
  note: z.string().max(ICERTIS_UPLOAD_NOTE_MAX_LENGTH).optional(),
});

export type IcertisUploadRequest = z.infer<typeof IcertisUploadRequestSchema>;

export const IcertisRawLookupResponseSchema = z.object({
  Data: z.array(z.record(z.string(), z.unknown())).optional(),
});

export const IcertisLookupOptionsResponseSchema = z.object({
  rows: z.array(z.record(z.string(), z.unknown())),
  displayColumn: z.string().nullable(),
});

export const AgreementPreviewDataSchema = z.object({
  name: z.string().nullable().optional(),
  business_status: z.string().nullable().optional(),
  contract_type_name: z.string().nullable().optional(),
  agreement_id: z.string(),
  template_name: z.string().nullable().optional(),
  deeplink: HttpsUrlSchema.nullable().optional(),
  artifact_id: z.string().nullable().optional(),
  document_ready: z.boolean().optional(),
});
